import React, { useState } from 'react';
import axios from 'axios';
import SEO from '../components/SEO';
import StandardsSection from '../components/StandardsSection';
import {
  FaArrowRight,
  FaAward,
  FaBolt,
  FaCheckCircle,
  FaClipboardCheck,
  FaCertificate,
  FaGraduationCap,
  FaHardHat,
  FaShieldAlt,
  FaTools,
  FaUsers,
  FaTimes,
  FaPhone,
} from 'react-icons/fa';

import '../styles/services-page.css';

import servicesHeroImage from '../pics/serviceimg.png';
import serviceImageOne from '../pics/crane.png';
import serviceImageTwo from '../pics/liftinginspection.png';
import serviceImageThree from '../pics/load test3.webp';
import serviceImageFour from '../pics/pressure pump on site.webp';

const API_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function ServicesPage() {
  const [activeTab, setActiveTab] = useState('services');
  const [formMode, setFormMode] = useState('inspection');
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const scrollToSection = (id, tab = activeTab) => {
    setActiveTab(tab);

    const element = document.getElementById(id);

    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (sending) return;

    const formData = new FormData(e.target);
    const isTraining = formMode === 'training';

    const service = `${formData.get('service') || ''}`.trim();
    const details = `${formData.get('details') || ''}`.trim();
    const requirements = `${formData.get('requirements') || ''}`.trim();

    const enquiryType = isTraining
      ? 'Training Enquiry'
      : 'Service Request';

    const message = [
      `Enquiry type: ${enquiryType}`,
      `${isTraining ? 'Training programme' : 'Service required'}: ${service || 'Not specified'}`,
      `${isTraining ? 'Number of participants' : 'Project / work location'}: ${details || 'Not specified'}`,
      '',
      'Additional requirements:',
      requirements || 'None provided.'
    ].join('\n');

    const payload = {
      name: formData.get('name') || '',
      email: formData.get('email') || '',
      phone: formData.get('phone') || '',
      company: formData.get('company') || '',
      service,
      subject: enquiryType,
      message,
      honeypot: formData.get('honeypot') || ''
    };

    setSending(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_URL}/contact`,
        payload,
        { timeout: 20000 }
      );

      if (response.data?.success) {
        setSubmitted(true);
      } else {
        setError(
          response.data?.message ||
          'We could not send your request. Please try again.'
        );
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
        'We could not send your request. Please check your connection and try again.'
      );
    } finally {
      setSending(false);
    }
  };

  const services = [
    {
      number: '01',
      title: 'Mobile Crane Operator Training',
      label: 'OPERATOR TRAINING',
      desc:
        'Comprehensive practical and theoretical training designed to prepare mobile crane operators for safe and effective lifting operations.',
      image: serviceImageOne,
      icon: <FaGraduationCap />,
      accent: 'primary',
      focus: 'Operator Competence',
      deliverables: [
        'Practical operator training',
        'Theoretical safety instruction',
        'Equipment and load awareness',
      ],
    },
    {
      number: '02',
      title: 'Crane Inspector Certification',
      label: 'INSPECTION & CERTIFICATION',
      desc:
        'Structured inspection programmes supporting equipment integrity, operational safety, and clear certification documentation.',
      image: serviceImageTwo,
      icon: <FaClipboardCheck />,
      accent: 'blue',
      focus: 'Equipment Assurance',
      deliverables: [
        'Crane inspection programmes',
        'Inspection documentation',
        'Certification support',
      ],
    },
    {
      number: '03',
      title: 'Safe Rigging & Lifting',
      label: 'RIGGING & LIFTING',
      desc:
        'Focused instruction covering rigging techniques, load management, lifting practices, and practical safety considerations.',
      image: serviceImageThree,
      icon: <FaHardHat />,
      accent: 'red',
      focus: 'Safe Lifting Practice',
      deliverables: [
        'Rigging techniques',
        'Load management',
        'Safe lifting practices',
      ],
    },
    {
      number: '04',
      title: 'Equipment Inspection & Certification',
      label: 'EQUIPMENT SERVICES',
      desc:
        'End-to-end equipment inspection and certification services delivered with clear documentation and technical confidence.',
      image: serviceImageFour,
      icon: <FaTools />,
      accent: 'dark',
      focus: 'Inspection & Documentation',
      deliverables: [
        'Equipment inspection',
        'Certification documentation',
        'Operational safety support',
      ],
    },
  ];

  const assuranceSteps = [
    {
      number: '01',
      title: 'Initial Assessment',
      desc:
        'We review the equipment, personnel, training requirements, or project scope to establish the appropriate service approach.',
      icon: <FaClipboardCheck />,
      result: 'Scope & Requirement Review',
    },
    {
      number: '02',
      title: 'Technical Preparation',
      desc:
        'Our team prepares the appropriate personnel, equipment, training materials, and inspection requirements for the assignment.',
      icon: <FaTools />,
      result: 'Prepared Service Delivery',
    },
    {
      number: '03',
      title: 'Practical Execution',
      desc:
        'Training, inspection, testing, or certification activities are carried out with emphasis on safety, accuracy, and practical competence.',
      icon: <FaHardHat />,
      result: 'Field-Level Execution',
    },
    {
      number: '04',
      title: 'Documentation & Support',
      desc:
        'Relevant results, assessments, and certification documentation are compiled to support operational records and client requirements.',
      icon: <FaCertificate />,
      result: 'Clear Documentation',
    },
  ];

  const trainingPrograms = [
    {
      level: 'CORE PROGRAM',
      title: 'Mobile Crane Operator Training',
      desc:
        'Practical and theoretical training focused on safe crane operation, lifting awareness, equipment checks, and responsible operating practices.',
      modules: [
        'Operator safety fundamentals',
        'Pre-use equipment checks',
        'Load and lifting awareness',
        'Safe operating procedures',
      ],
    },
    {
      level: 'CORE PROGRAM',
      title: 'Rigging & Lifting Training',
      desc:
        'Hands-on instruction for personnel involved in rigging, lifting, load handling, and field support activities.',
      modules: [
        'Rigging equipment awareness',
        'Load handling practices',
        'Slinging techniques',
        'Communication during lifting',
      ],
    },
    {
      level: 'INSPECTION PROGRAM',
      title: 'Crane Inspector Certification',
      desc:
        'Structured training and certification preparation for personnel developing competence in crane and lifting equipment inspection.',
      modules: [
        'Inspection principles',
        'Defect identification',
        'Inspection documentation',
        'Certification procedures',
      ],
    },
    {
      level: 'EQUIPMENT PROGRAM',
      title: 'Equipment Inspection & Certification',
      desc:
        'Inspection-focused services designed to help organisations maintain confidence in lifting equipment condition and documentation.',
      modules: [
        'Equipment assessment',
        'Inspection reporting',
        'Certification records',
        'Operational recommendations',
      ],
    },
  ];

  const facilities = [
    {
      image: serviceImageOne,
      tag: 'CRANE OPERATIONS',
      title: 'Crane Training & Practical Operations',
      desc:
        'Practical learning environment supporting crane operator development and safe lifting procedures.',
      location: 'Training & Field Operations',
    },
    {
      image: serviceImageTwo,
      tag: 'INSPECTION',
      title: 'Lifting Equipment Inspection',
      desc:
        'Inspection-focused activities covering lifting equipment assessment, condition checks, and documentation.',
      location: 'Inspection Services',
    },
    {
      image: serviceImageThree,
      tag: 'LOAD TESTING',
      title: 'Load Testing & Lifting Support',
      desc:
        'Practical load testing and lifting support using appropriate procedures and controlled operations.',
      location: 'Field Service',
    },
  ];

  return (
    <div className="services-page">
      <SEO
        title="Our Services"
        description="Explore Dofcan Oilserve's services: crane operations, load testing, pressure testing, calibration and lifting equipment inspection for the oil and gas industry."
        path="/services"
      />

      {/* =====================================================
          HERO
      ====================================================== */}
      <section className="services-hero">
        <div className="services-hero-pattern"></div>

        <div className="services-container">

          <div className="services-breadcrumb">
            <span>Dofcan Oilserve Limited</span>
            <FaArrowRight />
            <strong>Services</strong>

            <span className="services-status">
              <span></span>
              Service Operations Active
            </span>
          </div>

          <div className="services-hero-grid">

            <div className="services-hero-content">

              <div className="services-eyebrow">
                <span></span>
                Training • Inspection • Certification
              </div>

              <h1>
                Professional Services for
                <br />
                Safer Lifting Operations
              </h1>

              <p>
                We support organisations and professionals through
                practical training, lifting equipment inspection,
                certification, and safety-focused operational
                services.
              </p>

              <div className="services-hero-buttons">
                <button
                  type="button"
                  onClick={() =>
                    scrollToSection('service-portfolio', 'services')
                  }
                  className="services-btn-primary"
                >
                  Explore Our Services
                  <FaArrowRight />
                </button>

                <button
                  type="button"
                  onClick={() =>
                    scrollToSection('request-service', 'request')
                  }
                  className="services-btn-secondary"
                >
                  Request a Service
                  <FaClipboardCheck />
                </button>
              </div>
            </div>

            <div className="services-hero-visual">
              <img
                src={servicesHeroImage}
                alt="Dofcan Oilserve services"
              />

              <div className="services-hero-image-overlay"></div>

              <div className="services-hero-image-caption">
                <FaShieldAlt />

                <div>
                  <span>DOFCAN SERVICE STANDARD</span>
                  <strong>Safety • Competence • Confidence</strong>
                </div>
              </div>
            </div>
          </div>

          {/* Hero Metrics */}
          <div className="services-metric-grid">

            <div className="services-metric">
              <div className="services-metric-top">
                <span>Experience</span>
                <FaAward />
              </div>
              <strong>15+</strong>
              <p>Years of industry experience</p>
            </div>

            <div className="services-metric">
              <div className="services-metric-top">
                <span>Professionals</span>
                <FaUsers />
              </div>
              <strong>3k+</strong>
              <p>Professionals trained</p>
            </div>

            <div className="services-metric">
              <div className="services-metric-top">
                <span>Companies</span>
                <FaHardHat />
              </div>
              <strong>500+</strong>
              <p>Companies served</p>
            </div>

            <div className="services-metric">
              <div className="services-metric-top">
                <span>Service Areas</span>
                <FaTools />
              </div>
              <strong>4</strong>
              <p>Core service categories</p>
            </div>

          </div>

          {/* Discipline Navigation */}
          <div className="services-discipline-nav">
            <span>Service Directory:</span>

            <button
              className={activeTab === 'services' ? 'active' : ''}
              onClick={() =>
                scrollToSection('service-portfolio', 'services')
              }
            >
              All Services
            </button>

            <button
              onClick={() =>
                scrollToSection('service-portfolio', 'services')
              }
            >
              Training
            </button>

            <button
              onClick={() =>
                scrollToSection('service-portfolio', 'services')
              }
            >
              Inspection
            </button>

            <button
              onClick={() =>
                scrollToSection('service-portfolio', 'services')
              }
            >
              Certification
            </button>

            <button
              onClick={() =>
                scrollToSection('assurance-standard', 'assurance')
              }
            >
              Service Process
            </button>
          </div>
        </div>
      </section>

      {/* =====================================================
          SERVICE PORTFOLIO
      ====================================================== */}
      <section
        id="service-portfolio"
        className="services-portfolio-section"
      >
        <div className="services-container">

          <div className="services-section-heading">
            <div>
              <div className="services-section-label">
                <span></span>
                Our Service Portfolio
              </div>

              <h2>
                Training, Inspection &amp;
                <br />
                Certification Services
              </h2>
            </div>

            <p>
              Our services are structured around building competent
              personnel, supporting equipment integrity, and
              strengthening safety across lifting operations.
            </p>
          </div>

          <div className="services-portfolio-grid">

            {services.map((service) => (
              <article
                key={service.number}
                className={`service-detail-card ${service.accent}`}
              >
                <div className="service-card-image">
                  <img
                    src={service.image}
                    alt={service.title}
                  />

                  <div className="service-card-image-overlay"></div>

                  <span className="service-card-number">
                    {service.number}
                  </span>

                  <span className="service-card-tag">
                    {service.label}
                  </span>
                </div>

                <div className="service-card-content">

                  <div className="service-card-heading">
                    <div className="service-card-icon">
                      {service.icon}
                    </div>

                    <div>
                      <span className="service-card-label">
                        {service.label}
                      </span>

                      <h3>{service.title}</h3>
                    </div>
                  </div>

                  <p>{service.desc}</p>

                  <div className="service-card-info">
                    <div>
                      <span>Primary Focus</span>
                      <strong>{service.focus}</strong>
                    </div>
                  </div>

                  <div className="service-deliverables">
                    <span>Service Includes</span>

                    {service.deliverables.map((item) => (
                      <div key={item}>
                        <FaCheckCircle />
                        {item}
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      scrollToSection('request-service', 'request')
                    }
                    className="service-card-button"
                  >
                    Request This Service
                    <FaArrowRight />
                  </button>

                </div>
              </article>
            ))}

          </div>
        </div>
      </section>

      {/* =====================================================
          ASSURANCE PROCESS
      ====================================================== */}
      <section
        id="assurance-standard"
        className="services-assurance-section"
      >
        <div className="services-container">

          <div className="services-centred-heading">
            <span>THE DOFCAN SERVICE APPROACH</span>

            <h2>
              Structured Service Delivery
            </h2>

            <p>
              Every assignment is approached through a clear
              sequence designed to understand requirements,
              prepare appropriately, execute safely, and provide
              useful documentation.
            </p>
          </div>

          <div className="services-assurance-grid">

            {assuranceSteps.map((step) => (
              <article
                className="assurance-card"
                key={step.number}
              >
                <span className="assurance-number">
                  {step.number}
                </span>

                <div className="assurance-icon">
                  {step.icon}
                </div>

                <h3>{step.title}</h3>

                <p>{step.desc}</p>

                <div className="assurance-result">
                  <span>OUTPUT</span>
                  <strong>{step.result}</strong>
                </div>
              </article>
            ))}

          </div>

          <StandardsSection
            variant="strip"
            eyebrow="Standards Behind Our Services"
            descriptions={[
              'Quality-focused inspection, certification, training, and service delivery.',
              'Responsible environmental practices and consideration of environmental impact.',
              'Safety-focused training, inspection, certification, and operational practices.',
            ]}
            note="Our services are delivered in line with these management principles. This reflects how we work rather than a certification held for any individual service."
            className="services-standards-strip"
          />
        </div>
      </section>

      {/* =====================================================
          TRAINING PROGRAMMES
      ====================================================== */}
      <section className="services-training-section" data-reveal>
        <div className="services-container">

          <div className="services-section-heading">
            <div>
              <div className="services-section-label">
                <span></span>
                Workforce Development
              </div>

              <h2>
                Training &amp; Competency
                <br />
                Programmes
              </h2>
            </div>

            <p>
              Practical learning programmes designed for operators,
              lifting personnel, inspectors, supervisors, and
              professionals working within industrial operations.
            </p>
          </div>

          <div className="training-program-grid" data-reveal="stagger">

            {trainingPrograms.map((program, index) => (
              <article
                className="training-program-card"
                key={program.title}
              >
                <div className="training-program-top">
                  <span>{program.level}</span>
                  <strong>
                    {String(index + 1).padStart(2, '0')}
                  </strong>
                </div>

                <h3>{program.title}</h3>

                <p>{program.desc}</p>

                <div className="training-module-box">
                  <span>PROGRAMME AREAS</span>

                  {program.modules.map((module) => (
                    <div key={module}>
                      <span></span>
                      {module}
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setFormMode('training');
                    scrollToSection('request-service', 'request');
                  }}
                  className="training-enrol-button"
                >
                  Enquire About Training
                  <FaArrowRight />
                </button>
              </article>
            ))}

          </div>
        </div>
      </section>

      {/* =====================================================
          IMAGE SHOWCASE
      ====================================================== */}
      <section className="services-showcase-section" data-reveal="soft">
        <div className="services-container">

          <div className="services-section-heading">
            <div>
              <div className="services-section-label">
                <span></span>
                Practical Service Delivery
              </div>

              <h2>
                Training, Inspection &amp;
                <br />
                Field Operations
              </h2>
            </div>

            <p>
              Our service delivery combines practical knowledge,
              technical processes, and safety-focused field
              operations.
            </p>
          </div>

          <div className="services-showcase-grid">

            {facilities.map((facility) => (
              <article
                className="service-showcase-card"
                key={facility.title}
              >
                <div className="service-showcase-image">
                  <img
                    src={facility.image}
                    alt={facility.title}
                  />

                  <span>{facility.tag}</span>
                </div>

                <div className="service-showcase-content">
                  <h3>{facility.title}</h3>

                  <p>{facility.desc}</p>

                  <div>
                    <span>{facility.location}</span>
                    <FaArrowRight />
                  </div>
                </div>
              </article>
            ))}

          </div>
        </div>
      </section>

      {/* =====================================================
          REQUEST SERVICE
      ====================================================== */}
      <section
        id="request-service"
        className="services-request-section"
      >
        <div className="services-container">

          <div className="services-request-wrapper">

            {/* Left Panel */}
            <div className="services-request-info">

              <div className="request-badge">
                <FaBolt />
                Service Request Desk
              </div>

              <h2>
                Request a Service
                <br />
                or Training Programme
              </h2>

              <p>
                Tell us what you need and our team can review your
                requirements and provide the appropriate next steps.
              </p>

              <div className="request-contact-list">

                <div>
                  <span>
                    <FaPhone />
                  </span>

                  <section>
                    <small>Contact Our Team</small>
                    <strong>Speak with DOFCAN</strong>
                  </section>
                </div>

                <div>
                  <span>
                    <FaCertificate />
                  </span>

                  <section>
                    <small>Professional Services</small>
                    <strong>
                      Training • Inspection • Certification
                    </strong>
                  </section>
                </div>

                <div>
                  <span>
                    <FaShieldAlt />
                  </span>

                  <section>
                    <small>Our Focus</small>
                    <strong>
                      Safer &amp; Competent Operations
                    </strong>
                  </section>
                </div>

              </div>

              <div className="request-bottom-note">
                <FaCheckCircle />
                Clear requirements. Practical solutions.
              </div>
            </div>

            {/* Right Form */}
            <div className="services-request-form">

              <div className="request-tabs">
                <button
                  type="button"
                  className={
                    formMode === 'inspection'
                      ? 'active'
                      : ''
                  }
                  onClick={() => {
                    setFormMode('inspection');
                    setSubmitted(false);
                  }}
                >
                  Request Inspection / Service
                </button>

                <button
                  type="button"
                  className={
                    formMode === 'training'
                      ? 'active'
                      : ''
                  }
                  onClick={() => {
                    setFormMode('training');
                    setSubmitted(false);
                  }}
                >
                  Enquire About Training
                </button>
              </div>

              {!submitted ? (
                <form onSubmit={handleSubmit}>

                  <div className="request-form-grid">

                    <div className="request-field">
                      <label>
                        Company / Organisation
                      </label>

                      <input
                        required
                        type="text"
                        name="company"
                        placeholder="Company or organisation name"
                      />
                    </div>

                    <div className="request-field">
                      <label>
                        Contact Name
                      </label>

                      <input
                        required
                        type="text"
                        name="name"
                        placeholder="Full name"
                      />
                    </div>

                  </div>

                  <div className="request-form-grid">

                    <div className="request-field">
                      <label>
                        Email Address
                      </label>

                      <input
                        required
                        type="email"
                        name="email"
                        placeholder="name@company.com"
                      />
                    </div>

                    <div className="request-field">
                      <label>
                        Phone Number
                      </label>

                      <input
                        type="tel"
                        name="phone"
                        placeholder="+234..."
                      />
                    </div>

                  </div>

                  {formMode === 'inspection' ? (
                    <>
                      <div className="request-form-grid">

                        <div className="request-field">
                          <label>
                            Service Required
                          </label>

                          <select name="service" defaultValue="">
                            <option value="" disabled>
                              Select a service
                            </option>

                            <option>
                              Mobile Crane Operator Training
                            </option>

                            <option>
                              Crane Inspector Certification
                            </option>

                            <option>
                              Safe Rigging &amp; Lifting
                            </option>

                            <option>
                              Equipment Inspection &amp; Certification
                            </option>
                          </select>
                        </div>

                        <div className="request-field">
                          <label>
                            Project / Work Location
                          </label>

                          <input
                            type="text"
                            name="details"
                            placeholder="Location"
                          />
                        </div>

                      </div>
                    </>
                  ) : (
                    <>
                      <div className="request-form-grid">

                        <div className="request-field">
                          <label>
                            Training Programme
                          </label>

                          <select name="service" defaultValue="">
                            <option value="" disabled>
                              Select training
                            </option>

                            <option>
                              Mobile Crane Operator Training
                            </option>

                            <option>
                              Rigging &amp; Lifting Training
                            </option>

                            <option>
                              Crane Inspector Certification
                            </option>

                            <option>
                              Equipment Inspection &amp; Certification
                            </option>
                          </select>
                        </div>

                        <div className="request-field">
                          <label>
                            Number of Participants
                          </label>

                          <input
                            type="number"
                            name="details"
                            min="1"
                            placeholder="Number of trainees"
                          />
                        </div>

                      </div>
                    </>
                  )}

                  <div className="request-field">
                    <label>
                      Additional Requirements
                    </label>

                    <textarea
                      rows="4"
                      name="requirements"
                      placeholder="Tell us about your requirements..."
                    ></textarea>
                  </div>

                  {/* Spam trap: hidden from real visitors */}
                  <input
                    type="text"
                    name="honeypot"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    style={{ display: 'none' }}
                  />

                  {error && (
                    <p className="request-form-error" role="alert">
                      {error}
                    </p>
                  )}

                  <div className="request-form-bottom">

                    <span>
                      <FaShieldAlt />
                      Your request will be handled confidentially.
                    </span>

                    <button type="submit" disabled={sending}>
                      {sending
                        ? 'Sending...'
                        : formMode === 'inspection'
                          ? 'Submit Service Request'
                          : 'Submit Training Enquiry'}

                      <FaArrowRight />
                    </button>

                  </div>

                </form>
              ) : (
                <div className="request-success">

                  <FaCheckCircle />

                  <h3>
                    Request Received
                  </h3>

                  <p>
                    Thank you for contacting Dofcan Oilserve
                    Limited. Your request has been sent to our
                    team and we will review your requirements.
                  </p>

                  <button
                    type="button"
                    onClick={() => {
                      setSubmitted(false);
                      setError(null);
                    }}
                  >
                    Submit Another Request
                  </button>

                </div>
              )}

            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          FINAL CTA
      ====================================================== */}
      <section className="services-final-cta" data-reveal>

        <div className="services-container">

          <div className="services-final-grid">

            <div>
              <span>
                DOFCAN OILSERVE LIMITED
              </span>

              <h2>
                Let&apos;s Build Safer
                <br />
                Lifting Operations.
              </h2>

              <p>
                From professional training to equipment inspection
                and certification, we support safer and more
                competent industrial operations.
              </p>
            </div>

            <div className="services-final-actions">

              <button
                type="button"
                onClick={() =>
                  scrollToSection('request-service', 'request')
                }
              >
                Request a Service
                <FaArrowRight />
              </button>

              <button
                type="button"
                onClick={() =>
                  scrollToSection(
                    'service-portfolio',
                    'services'
                  )
                }
              >
                View Services
                <FaTools />
              </button>

            </div>

          </div>

        </div>
      </section>

      {/* Modal-ready close reference kept for future expansion */}
      <span className="services-hidden-icon">
        <FaTimes />
      </span>

    </div>
  );
}